/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCustomContent } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { VisualizedLinkedEntitiesResult } from 'src/languageStudio/appShell/prebuiltDemos/linkedEntitiesDemo/VisualizedLinkedEntitiesResult';
import { textAnalyticsDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    linkedEntitiesApiReferenceLink,
    linkedEntitiesDocLink,
    linkedEntitiesOverviewLink,
    linkedEntitiesSamplesLink,
    linkedEntitiesSdkLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { Language, linkedEntitiesDemoSupportedLanguages } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { useTextAnalyticsCapabilities } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { LinkedEntitiesDocuments, LinkedEntity } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized, LinkedEntityMatchesPair } from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    pageHeader: {
        id: 'languageStudio.linedEntitiesDemoPage.pageHeader',
        defaultMessage: 'Find linked entities'
    },
    capabilityDescription: {
        id: 'languageStudio.linedEntitiesDemoPage.capabilityDescription',
        defaultMessage: `This prebuilt capability disambiguates the identity of an entity found
        in text by linking to a knowledge base. Text Analytics' Entity Linking feature provides
        links to Wikipedia as its knowledge base.`
    },
    emptyState: {
        id: 'languageStudio.linedEntitiesDemoPage.emptyState',
        defaultMessage: `Your linked entities results will appear here once you enter or upload
        some text and press Run. The file format needs to be .txt`
    },
    signedOutEmptyState: {
        id: 'languageStudio.linedEntitiesDemoPage.signedOutEmptyState',
        defaultMessage: `Your linked entities results will appear here once you press Run.`
    }
});

const flattenLinkedEntities = (entities: LinkedEntity[]): LinkedEntityMatchesPair[] =>
    entities.map(entity => entity.matches.reduce((acc, m) => [...acc, { entity, match: m }], [])).reduce((acc, e) => [...acc, ...e], []);

/**
 * Displays the Linked Entities demo page.
 */
export const LinkedEntitiesDemoPage = observer(() => {
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const { isSignedIn } = rootStore.authStore;
    const { tracker, resetResults, getCurlCommand, linkedEntities, getLinkedEntities, detectedLanguages } = useTextAnalyticsCapabilities();

    const [document, setDocument] = React.useState('');
    const [language, setLanguage] = React.useState<Language['code']>('en');
    const [languageAutoDetection, setLanguageAutoDetection] = React.useState(false);
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);

    const handleDocumentChange = (newDocument: string) => {
        if (linkedEntities) {
            resetResults();
        }
        setDocument(newDocument);
    };

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent('Linked Entities');
        }

        getLinkedEntities({
            document,
            language,
            sampleTitle: selectedSampleTitle,
            autoDetectLanguage: languageAutoDetection
        });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                featureName="Linked Entities"
                demoVersion={LuisConstants.Demos.ApiVersions.GA}
                supportedPlatforms={['cloud']}
                getSdkLink={linkedEntitiesSdkLink}
                demoTitle={localize(messages.pageHeader)}
                documentationLink={linkedEntitiesDocLink}
                learnMoreLink={linkedEntitiesOverviewLink}
                gitHubSamplesLink={linkedEntitiesSamplesLink}
                demoDescription={localize(messages.capabilityDescription)}
            />

            <DocumentInputStep
                document={document}
                selectedLanguage={language}
                featureName="Linked Entities"
                onLanguageChange={setLanguage}
                acceptedResourceType="TextAnalytics"
                samplesList={textAnalyticsDemoSamples}
                onDocumentChange={handleDocumentChange}
                selectedSampleTitle={selectedSampleTitle}
                supportedRegionLink={linkedEntitiesApiReferenceLink}
                languagesList={linkedEntitiesDemoSupportedLanguages}
                onLanguageAutoDetectionClick={setLanguageAutoDetection}
                onSampleClick={sample => setSelectedSampleTitle(sample?.title)}
                isTextRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}>
                <DemoPageAgreement
                    featureName="Linked Entities"
                    onRunDemoClick={handleRunDemo}
                    isRunDisabled={!document.length}
                    acceptedResourceType="TextAnalytics"
                    pricingLink={textAnalyticsPricingLink}
                />
            </DocumentInputStep>

            <ResultsStep<LinkedEntitiesDocuments>
                results={linkedEntities}
                featureName="Linked Entities"
                isLoading={tracker.isRunning}
                emptyStateMessage={localize(isSignedIn ? messages.emptyState : messages.signedOutEmptyState)}>
                <VisualizedLinkedEntitiesResult
                    document={document}
                    detectedLanguage={detectedLanguages}
                    entities={linkedEntities && flattenLinkedEntities(linkedEntities.documents[0].entities)}
                    isLabelerRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}
                    isLabelerWordTokenized={isDocumentSwaggerWordTokenized(
                        detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                    )}
                />
            </ResultsStep>

            <NextStepsStep
                featureName="Linked Entities"
                sdkLink={linkedEntitiesSdkLink}
                isResultsEmpty={!linkedEntities}
                documentationLink={linkedEntitiesDocLink}
                sampleCodeLink={linkedEntitiesSamplesLink}
                demosToDisplay={['ner', 'pii', 'keyPhrases']}
                curlCommand={getCurlCommand({ language, text: document, languageAutoDetection, endpoint: 'EntityLinking' })}
            />
        </Stack>
    );
});
